import { createRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { AnimatePresence } from "motion/react";
import { Screen, Skeleton, type Tab } from "../../shared/ui/index.js";
import { Pane } from "../../shared/motion.js";
import { fmtDate } from "../../shared/format.js";
import { Shell } from "../../app/Shell.js";
import { requireAuth } from "../../app/guards.js";
import { rootRoute } from "../../app/root-route.js";
import { useEvent } from "./events.api.js";
import { QrPanel } from "./QrPanel.js";
import { ThemePanel } from "./ThemePanel.js";
import { GalleryPanel } from "./GalleryPanel.js";
import { GuestsPanel } from "./GuestsPanel.js";
import { WallPanel } from "./WallPanel.js";
import { OwnerWallPanel } from "./OwnerWallPanel.js";
import { EventSettingsPanel } from "./EventSettingsPanel.js";

export const appEventRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/app/events/$id",
  beforeLoad: requireAuth(),
  component: () => (
    <Shell>
      <EventScreen />
    </Shell>
  ),
});

const BASE_TABS: Tab[] = [
  { id: "qr", label: "QR code" },
  { id: "projection", label: "Projection" },
  { id: "gallery", label: "Galerie" },
  { id: "guests", label: "Invités" },
  { id: "theme", label: "Thème" },
  { id: "settings", label: "Réglages" },
];

/** Onglet « Mur » inséré après « Galerie » quand le mur des invités est activé. */
function tabsFor(wallEnabled: boolean): Tab[] {
  if (!wallEnabled) return BASE_TABS;
  const i = BASE_TABS.findIndex((t) => t.id === "gallery");
  return [
    ...BASE_TABS.slice(0, i + 1),
    { id: "wall", label: "Mur" },
    ...BASE_TABS.slice(i + 1),
  ];
}

function EventScreen() {
  const { id } = appEventRoute.useParams();
  const { data, isLoading } = useEvent(id);
  const [tab, setTab] = useState("qr");

  if (isLoading || !data) {
    return (
      <Screen title="…">
        <Skeleton count={2} className="h-48" />
      </Screen>
    );
  }

  const e = data.event;

  return (
    <Screen
      title={e.name}
      subtitle={
        <>
          {fmtDate(e.date)} · révélé le {fmtDate(e.revealAt)} ·{" "}
          <Link
            to="/app/weddings/$id"
            params={{ id: e.weddingId }}
            className="underline hover:text-stone-700"
          >
            {data.wedding.partner1Name} &amp; {data.wedding.partner2Name}
          </Link>
        </>
      }
      tabs={tabsFor(e.wallEnabled)}
      activeTab={tab}
      onTab={setTab}
    >
      <AnimatePresence mode="wait">
        {tab === "qr" && (
          <Pane id="qr">
            <QrPanel eventId={id} />
          </Pane>
        )}
        {tab === "projection" && (
          <Pane id="projection">
            <WallPanel event={e} />
          </Pane>
        )}
        {tab === "theme" && (
          <Pane id="theme">
            <ThemePanel eventId={id} theme={e.theme} />
          </Pane>
        )}
        {tab === "gallery" && (
          <Pane id="gallery">
            <GalleryPanel eventId={id} />
          </Pane>
        )}
        {tab === "wall" && e.wallEnabled && (
          <Pane id="wall">
            <OwnerWallPanel eventId={id} theme={e.theme} />
          </Pane>
        )}
        {tab === "guests" && (
          <Pane id="guests">
            <GuestsPanel eventId={id} />
          </Pane>
        )}
        {tab === "settings" && (
          <Pane id="settings">
            <EventSettingsPanel event={e} />
          </Pane>
        )}
      </AnimatePresence>
    </Screen>
  );
}
